import React, { useState } from "react";
import { object, array, oneOf } from "prop-types";
import { Form, AutoComplete, Typography } from "antd";
import { useWatch } from "antd/lib/form/Form";
import SelectionGroupTag from "./SelectionGroupTag";
import {
  SELECTION_GROUP_PRESETS,
  SELECTION_GROUP_RESERVED,
  SELECTION_GROUP_COLORS,
  SELECTION_GROUP_DESCRIPTIONS,
  SELECTION_GROUP_REGEX,
  getPresetSuggestions,
  getUsedSelectionGroups,
  normalizeSelectionGroup,
} from "../../utils/selectionGroups";
import { defaultFormItemLayout } from "../../utils/formLayout";

const SELECTION_GROUP_TOOLTIP =
  "Groups campaigns together for selection limits. User can select only one per group. Pick a preset or type a custom handle (3-30 chars: letters, numbers, . and _).";

const SELECTION_GROUP_INLINE_HELP =
  "You can edit this later from the contract view.";

const selectionGroupRowStyle = {
  display: "flex",
  gap: 24,
  width: "100%",
  alignItems: "flex-start",
};
// minHeight reserves one line for the validation error so showing it never resizes the modal.
const selectionGroupInputCol = { width: "calc(50% - 12px)", minHeight: 88 };
// Preview sits beside the input (offset past the label) using the row's spare right-hand width.
const centerFlexWrap = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
};

const selectionGroupPreviewStyle = {
  ...centerFlexWrap,
  gap: 8,
  marginTop: 34,
};
// Inline (wizard) preview sits on the same row as the input, no label offset.
const inlinePreviewStyle = {
  ...centerFlexWrap,
  gap: 8,
};
const inlineRowStyle = {
  ...centerFlexWrap,
  gap: 16,
};

// Blank clears to the default group; anything else must pass the shared handle regex.
const selectionGroupRules = [
  {
    validator: (_, value) =>
      !value || SELECTION_GROUP_REGEX.test(normalizeSelectionGroup(value))
        ? Promise.resolve()
        : Promise.reject(
            new Error("3-30 chars: letters, numbers, . and _ (no spaces)")
          ),
  },
];

const renderPresetOption = preset => ({
  value: preset,
  label: (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <SelectionGroupTag group={preset} />
      {SELECTION_GROUP_DESCRIPTIONS[preset] ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {SELECTION_GROUP_DESCRIPTIONS[preset]}
        </Typography.Text>
      ) : null}
    </span>
  ),
});

export default function SelectionGroupField({
  form,
  contractCampaigns,
  variant = "modal",
}) {
  // What the user is actively typing into the field. Drives the option filtering
  // (kept separate from the committed value so a selected group still shows the
  // full list for quick switching).
  const [selectionSearch, setSelectionSearch] = useState("");

  // Live value of the field so we can preview its color and surface close presets
  // as the user types (no need to save to see the color).
  const selectionGroupInput = useWatch("selectionGroup", form);

  const normalizedGroup = normalizeSelectionGroup(selectionGroupInput);
  const selectionGroupPreview = normalizedGroup || "default";
  // Only the actively-typed text filters the list; a blank search shows all.
  const searchKey = normalizeSelectionGroup(selectionSearch);
  const isSearchPreset = SELECTION_GROUP_PRESETS.includes(searchKey);
  const groupSuggestions = searchKey ? getPresetSuggestions(searchKey) : [];
  // Every group used by campaigns on this contract, most-used first.
  const contractUsedGroups = getUsedSelectionGroups(contractCampaigns);

  // Filter a preset list by the typed search text (substring), minus fuzzy
  // matches already surfaced under "Did you mean".
  const matchingPresets = presets =>
    searchKey
      ? presets.filter(
          preset =>
            !groupSuggestions.includes(preset) && preset.includes(searchKey)
        )
      : presets;

  const selectionGroupOptions = [];
  if (groupSuggestions.length) {
    selectionGroupOptions.push({
      label: "Did you mean",
      options: groupSuggestions.map(renderPresetOption),
    });
  }
  const contractGroupOptions = matchingPresets(contractUsedGroups);
  if (contractGroupOptions.length) {
    selectionGroupOptions.push({
      label: "Used in this contract",
      options: contractGroupOptions.map(renderPresetOption),
    });
  }
  // Presets already surfaced under "Used in this contract" are dropped here so
  // each option shows once.
  const reservedOptions = matchingPresets(SELECTION_GROUP_RESERVED).filter(
    preset => !contractUsedGroups.includes(preset)
  );
  if (reservedOptions.length) {
    selectionGroupOptions.push({
      label: "Reserved",
      options: reservedOptions.map(renderPresetOption),
    });
  }
  const colorOptions = matchingPresets(SELECTION_GROUP_COLORS).filter(
    preset => !contractUsedGroups.includes(preset)
  );
  if (colorOptions.length) {
    selectionGroupOptions.push({
      label: "Colors",
      options: colorOptions.map(renderPresetOption),
    });
  }
  // Offer the exact typed handle so its hashed color is visible before saving,
  // unless it's already listed above as a contract group.
  if (searchKey && !isSearchPreset && !contractUsedGroups.includes(searchKey)) {
    selectionGroupOptions.push({
      label: "Custom",
      options: [
        { value: searchKey, label: <SelectionGroupTag group={searchKey} /> },
      ],
    });
  }

  const selectionAutoComplete = (
    <AutoComplete
      allowClear
      placeholder="default"
      style={{ width: "100%" }}
      // We build the option list ourselves (fuzzy suggestions + custom
      // preview), so disable the built-in substring filter.
      filterOption={false}
      // Filtering follows what's typed; opening, selecting, or clearing
      // resets it so the full list is available for quick switching.
      onSearch={setSelectionSearch}
      onSelect={() => setSelectionSearch("")}
      onClear={() => setSelectionSearch("")}
      onDropdownVisibleChange={open => {
        if (open) setSelectionSearch("");
      }}
      // Size the dropdown to the preset list plus its group headers so the
      // last option isn't cut off by the default 256px scroll area.
      listHeight={(SELECTION_GROUP_PRESETS.length + 4) * 36}
      options={selectionGroupOptions}
    />
  );

  const selectionPreview = (
    <>
      <Typography.Text type="secondary">Preview:</Typography.Text>
      <SelectionGroupTag group={selectionGroupPreview} />
      {!normalizedGroup ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Blank uses the default group
        </Typography.Text>
      ) : null}
    </>
  );

  // Inline sits the field inside the surrounding horizontal form so its label
  // lines up with sibling fields; modal keeps a self-contained stacked layout.
  if (variant === "inline") {
    return (
      <Form.Item
        label="Selection Group"
        tooltip={SELECTION_GROUP_TOOLTIP}
        extra={SELECTION_GROUP_INLINE_HELP}
        {...defaultFormItemLayout}
      >
        <div style={inlineRowStyle}>
          <div style={{ flex: "1 1 260px", maxWidth: 360 }}>
            <Form.Item
              name="selectionGroup"
              noStyle
              rules={selectionGroupRules}
            >
              {selectionAutoComplete}
            </Form.Item>
          </div>
          <div style={inlinePreviewStyle}>{selectionPreview}</div>
        </div>
      </Form.Item>
    );
  }

  return (
    <div style={selectionGroupRowStyle}>
      <div style={selectionGroupInputCol}>
        <Form.Item
          label="Selection Group"
          name="selectionGroup"
          style={{ marginBottom: 0 }}
          tooltip={SELECTION_GROUP_TOOLTIP}
          rules={selectionGroupRules}
        >
          {selectionAutoComplete}
        </Form.Item>
      </div>
      <div style={selectionGroupPreviewStyle}>{selectionPreview}</div>
    </div>
  );
}

SelectionGroupField.propTypes = {
  form: object,
  contractCampaigns: array,
  variant: oneOf(["modal", "inline"]),
};
